import { formatTaskAssigneeOption, type OrgTask } from "./opsPaperworkTypes";
import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";
import {
  cardMutedTextClass,
  cardTitleTextClass,
  compactInputClass,
  insetSurfaceCardClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";

type Props = {
  uid: string;
  auth: any;
  scopedOrgId: string | null;
  taskAssigneeListError: string | null;
  taskAssigneeUseRawUuid: boolean;
  setTaskAssigneeUseRawUuid: (fn: (v: boolean) => boolean) => void;
  taskAssigneeLoading: boolean;
  taskAssigneeOptions: LeadTransferCandidate[];
  newTaskAssignee: string;
  setNewTaskAssignee: (v: string) => void;
  newTaskTitle: string;
  setNewTaskTitle: (v: string) => void;
  newTaskBody: string;
  setNewTaskBody: (v: string) => void;
  onCreateTask: () => Promise<void>;
  tasksInbox: OrgTask[];
  tasksSent: OrgTask[];
  onTaskAction: (task: OrgTask, action: string, extra?: Record<string, unknown>) => Promise<void>;
};

export function OpsPaperworkTasksTab(props: Props) {
  return (
    <div className="space-y-4">
      <div className={`${insetSurfaceCardClass} space-y-2`}>
        <h3 className={`text-sm font-semibold ${cardTitleTextClass}`}>New task</h3>
        {props.taskAssigneeListError ? <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200">Could not load org members ({props.taskAssigneeListError}). Use raw UUID below.</p> : null}
        {!props.scopedOrgId ? <p className={`text-xs ${cardMutedTextClass}`}>Select a workspace organization in the header to load assignees, or enter a user UUID below.</p> : null}
        {props.taskAssigneeUseRawUuid || props.taskAssigneeListError || !props.scopedOrgId ? (
          <input className={`w-full text-sm ${compactInputClass}`} placeholder="Assignee user UUID" value={props.newTaskAssignee} onChange={(e) => props.setNewTaskAssignee(e.target.value)} />
        ) : props.taskAssigneeLoading ? (
          <select disabled className={`w-full text-sm ${secondarySurfaceClass} px-2 py-1.5 ${cardMutedTextClass}`}><option>Loading org members…</option></select>
        ) : props.taskAssigneeOptions.length > 0 ? (
          <select className={`w-full text-sm ${compactInputClass}`} value={props.newTaskAssignee} onChange={(e) => props.setNewTaskAssignee(e.target.value)}>
            <option value="">— Select assignee —</option>
            {props.taskAssigneeOptions.map((m) => <option key={m.user_id} value={m.user_id}>{formatTaskAssigneeOption(m)}</option>)}
          </select>
        ) : (
          <>
            <p className={`text-xs ${cardMutedTextClass}`}>No directory rows returned. Enter assignee UUID.</p>
            <input className={`w-full text-sm ${compactInputClass}`} placeholder="Assignee user UUID" value={props.newTaskAssignee} onChange={(e) => props.setNewTaskAssignee(e.target.value)} />
          </>
        )}
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <button type="button" className="text-indigo-600 hover:underline" onClick={() => props.uid && props.setNewTaskAssignee(props.uid)}>Assign to me</button>
          {props.scopedOrgId && props.taskAssigneeOptions.length > 0 && !props.taskAssigneeListError ? <button type="button" className="text-gray-600 hover:underline" onClick={() => props.setTaskAssigneeUseRawUuid((v) => !v)}>{props.taskAssigneeUseRawUuid ? "Use member list" : "Paste UUID instead"}</button> : null}
        </div>
        <input className={`w-full text-sm ${compactInputClass}`} placeholder="Title" value={props.newTaskTitle} onChange={(e) => props.setNewTaskTitle(e.target.value)} />
        <textarea className={`w-full text-sm ${compactInputClass}`} placeholder="Details" value={props.newTaskBody} onChange={(e) => props.setNewTaskBody(e.target.value)} />
        <button type="button" onClick={() => void props.onCreateTask()} className="rounded bg-indigo-600 text-white text-sm px-3 py-1">Create</button>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-1">Inbox</h3>
        <ul className="space-y-2">
          {props.tasksInbox.map((t) => (
            <li key={t.id} className="border rounded-lg p-2 text-sm">
              <div className="font-medium">{t.title}</div><div className="text-xs text-gray-600">{t.body}</div>
              <div className="flex gap-2 mt-1 flex-wrap">
                {t.status === "open" ? <button type="button" className="text-xs text-indigo-600" onClick={() => void props.onTaskAction(t, "assignee_done", { assignee_note: "Done" })}>Mark done</button> : null}
                {t.status === "assignee_done" && t.issuer_user_id === props.uid ? <><button type="button" className="text-xs text-green-700" onClick={() => void props.onTaskAction(t, "issuer_closed")}>Close</button><button type="button" className="text-xs text-gray-600" onClick={() => void props.onTaskAction(t, "reopen")}>Reopen</button></> : null}
                {props.auth.hasRole("admin", "owner", "dev", "platform_admin", "platform_dev") && t.status !== "voided" && t.status !== "issuer_closed" ? <button type="button" className="text-xs text-red-600" onClick={() => void props.onTaskAction(t, "void", { void_reason: "Voided by administrator" })}>Void (admin)</button> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-1">Sent</h3>
        <ul className="space-y-1 text-sm text-gray-700">{props.tasksSent.map((t) => <li key={t.id}>{t.title} — {t.status}</li>)}</ul>
      </div>
    </div>
  );
}

